import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLang, useT } from '../context/LangContext';
import { 
  Search, 
  Eye, 
  MapPin, 
  ChevronRight,
  Loader2,
  PenLine,
  ArrowRight
} from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

var themeLabels = {
  land: 'Land', finance: 'Finance', inputs: 'Inputs',
  markets: 'Markets', climate: 'Climate', skills: 'Skills',
};

var countryFlags = {
  tanzania: '🇹🇿', ghana: '🇬🇭', malawi: '🇲🇼', rwanda: '🇷🇼',
  mali: '🇲🇱', 'burkina-faso': '🇧🇫', mozambique: '🇲🇿',
};

function StoryCard(props) {
  var story = props.story;
  var onClick = props.onClick;
  var byLabel = props.byLabel || 'by';
  var variant = props.variant || 'normal';
  var flag = countryFlags[story.country_id] || '🌍';
  var themeLabel = themeLabels[story.theme] || story.theme;
  var isTall = variant === 'tall';
  var isWide = variant === 'wide';

  return (
    <div
      className={'story-card-alive cursor-pointer overflow-hidden rounded-2xl bg-white border border-gray-100 h-full ' + (isTall ? 'bento-tall' : '') + (isWide ? 'bento-hero' : '')}
      onClick={onClick}
      data-testid={'story-card-' + story.id}
    >
      <div className={'relative overflow-hidden ' + (isTall ? 'h-full min-h-[400px]' : isWide ? 'h-full min-h-[300px]' : 'aspect-[4/3]')}>
        <img
          src={story.image_url || 'https://images.unsplash.com/photo-1551791506-775250793386?w=800'}
          alt={story.title}
          className="story-img w-full h-full object-cover"
          onError={(e) => {
            const fb = 'https://images.unsplash.com/photo-1551791506-775250793386?w=800';
            if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-base">{flag}</span>
            <Badge className="bg-white/20 backdrop-blur-sm text-white border-0 text-xs">{themeLabel}</Badge>
            {story.views > 0 && (
              <span className="text-white/50 text-xs flex items-center gap-1 ml-auto">
                <Eye className="w-3 h-3" />{story.views}
              </span>
            )}
          </div>
          <h3 className={'font-serif text-white leading-tight ' + (isTall || isWide ? 'text-xl md:text-2xl' : 'text-base')}>
            {story.title}
          </h3>
          {(isTall || isWide) && (
            <p className="text-white/60 text-sm mt-2 line-clamp-2">{story.caption}</p>
          )}
          {story.author_name && (
            <p className="text-white/40 text-xs mt-2">{byLabel} {story.author_name} — {story.country_name}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function StoriesHub() {
  var navigate = useNavigate();
  var lang = useLang().lang;
  var t = useT();
  var [stories, setStories] = useState([]);
  var [loading, setLoading] = useState(true);
  var [searchQuery, setSearchQuery] = useState('');
  var [selectedCountry, setSelectedCountry] = useState('all');
  var [selectedTheme, setSelectedTheme] = useState('all');

  useEffect(function() {
    fetchStories();
  }, [selectedCountry, selectedTheme, lang]);

  function fetchStories() {
    setLoading(true);
    var url = API_URL + '/api/stories/public?limit=50&lang=' + lang;
    if (selectedCountry !== 'all') url += '&country=' + selectedCountry;
    if (selectedTheme !== 'all') url += '&theme=' + selectedTheme;

    fetch(url)
      .then(function(r) { return r.json(); })
      .then(function(d) { setStories(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }

  var filteredStories = stories.filter(function(story) {
    if (!searchQuery.trim()) return true;
    var q = searchQuery.toLowerCase();
    return (story.title || '').toLowerCase().indexOf(q) >= 0 ||
           (story.caption || '').toLowerCase().indexOf(q) >= 0 ||
           (story.author_name || '').toLowerCase().indexOf(q) >= 0 ||
           (story.country_name || '').toLowerCase().indexOf(q) >= 0;
  });

  var countries = [
    { id: 'all', name: 'All', flag: '🌍' },
    { id: 'tanzania', name: 'Tanzania', flag: '🇹🇿' },
    { id: 'ghana', name: 'Ghana', flag: '🇬🇭' },
    { id: 'malawi', name: 'Malawi', flag: '🇲🇼' },
    { id: 'rwanda', name: 'Rwanda', flag: '🇷🇼' },
    { id: 'mali', name: 'Mali', flag: '🇲🇱' },
    { id: 'burkina-faso', name: 'Burkina Faso', flag: '🇧🇫' },
    { id: 'mozambique', name: 'Mozambique', flag: '🇲🇿' },
  ];

  var themes = [
    { id: 'all', name: 'All' },
    { id: 'land', name: 'Land' },
    { id: 'finance', name: 'Finance' },
    { id: 'inputs', name: 'Inputs' },
    { id: 'markets', name: 'Markets' },
    { id: 'climate', name: 'Climate' },
    { id: 'skills', name: 'Skills' },
  ];

  function getVariant(index) {
    if (index === 0) return 'wide';
    if (index === 3 || index === 6) return 'tall';
    return 'normal';
  }

  return (
    <section className="py-16" style={{ backgroundColor: '#F9F8F6' }} data-testid="stories-hub">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#72BF44] mb-3">{t('hub.eyebrow', 'Voices from the field')}</p>
          <h2 className="text-4xl sm:text-5xl font-serif text-gray-900 mb-4">
            {t('hub.title', 'Stories')}
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
            {t('hub.subtitle', 'Real voices from young farmers transforming agriculture. Each one a challenge, a breakthrough, a reason to believe.')}
          </p>
        </div>

        {/* Search & Filters */}
        <div className="max-w-4xl mx-auto mb-10">
          <div className="relative mb-5">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder={t('hub.search_placeholder', 'Search by name, country, or keyword...')}
              value={searchQuery}
              onChange={function(e) { setSearchQuery(e.target.value); }}
              className="pl-12 pr-4 py-4 text-base border border-gray-200 rounded-full bg-white focus:border-[#72BF44] focus:ring-[#72BF44]"
              data-testid="stories-search-input"
            />
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            {countries.map(function(c) {
              var isActive = selectedCountry === c.id;
              return (
                <button
                  key={c.id}
                  onClick={function() { setSelectedCountry(c.id); }}
                  className={'px-3 py-1.5 rounded-full text-sm font-medium transition-all ' + (isActive ? 'bg-[#07743F] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#72BF44]')}
                  data-testid={'filter-country-' + c.id}
                >
                  <span className="mr-1">{c.flag}</span>{c.name}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2">
            {themes.map(function(t) {
              var isActive = selectedTheme === t.id;
              return (
                <button
                  key={t.id}
                  onClick={function() { setSelectedTheme(t.id); }}
                  className={'px-3 py-1.5 rounded-full text-sm font-medium transition-all ' + (isActive ? 'bg-[#07743F] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#72BF44]')}
                  data-testid={'filter-theme-' + t.id}
                >
                  {t.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Results */}
        <div className="max-w-6xl mx-auto">
          <p className="text-sm text-gray-500 mb-6">
            {filteredStories.length} {filteredStories.length === 1 ? t('hub.voice_singular', 'voice') : t('hub.voices_plural', 'voices')} {t('hub.found', 'found')}
            {searchQuery ? ' ' + t('hub.for', 'for') + ' "' + searchQuery + '"' : ''}
          </p>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#07743F]" />
            </div>
          ) : filteredStories.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-serif text-gray-900 mb-2">{t('hub.no_voices_found', 'No voices found')}</h3>
              <p className="text-gray-500 mb-6">{t('hub.try_different_filters', 'Try different filters or search terms')}</p>
              <Button
                onClick={function() { setSearchQuery(''); setSelectedCountry('all'); setSelectedTheme('all'); }}
                variant="outline" className="border-[#07743F] text-[#07743F] rounded-full"
              >
                {t('hub.clear_filters', 'Clear filters')}
              </Button>
            </div>
          ) : (
            <div className="bento-grid">
              {filteredStories.map(function(story, index) {
                var variant = getVariant(index);
                return (
                  <div key={story.id} className="animate-discover" style={{ animationDelay: index * 0.06 + 's' }}>
                    <StoryCard
                      story={story}
                      variant={variant}
                      byLabel={t('common.by', 'by')}
                      onClick={function() { navigate('/story/' + story.id); }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="max-w-3xl mx-auto mt-20 text-center">
          <div className="bg-[#07743F] rounded-3xl p-10 md:p-14 relative overflow-hidden">
            <div className="absolute inset-0 rounded-3xl overflow-hidden">
              <img src="/images/stories-processing.jpg" alt="" className="w-full h-full object-cover opacity-20" aria-hidden="true" />
            </div>
            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-serif text-white mb-4">
                {t('hub.cta_title', 'Got a story to tell?')}
              </h3>
              <p className="text-green-200 mb-8 max-w-md mx-auto">
                {t('hub.cta_subtitle', 'Your voice could inspire thousands. No account needed. Start now.')}
              </p>
              <Button
                onClick={function() { navigate('/submit'); }}
                className="cta-alive bg-[#E4E517] text-[#07743F] hover:bg-[#d4d514] px-8 py-6 text-lg font-semibold rounded-full"
                data-testid="submit-story-cta"
              >
                <PenLine className="w-5 h-5 mr-2" />
                {t('cta.tell_your_story', 'Tell Your Story')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
